import React, { FC } from 'react';
import { Box } from '@mui/material';
import {
  ICandidateSourcingFilters,
  FilterKey,
  filterToLabel,
  ICandidateSourcingSearch
} from '../types';
import StyledModal from '../../Components/GenericModal/StyledModal';
import { sharedClasses } from '../../Components/CustomUIElements/sharedClasses';
import ModalFooterButtons from '../../Components/GenericModal/ModalFooterButtons';
import FilterSection from './FilterSection';
import { yearsOfExperienceOptions } from './filterConfig';
import { useFilterState } from './useFilterState';

interface EditFiltersModalProps {
  filterOptions?: ICandidateSourcingSearch['filter_options'];
  filters: ICandidateSourcingFilters;
  isOpen: boolean;
  onClose: () => void;
  onFiltersChange: (filters: ICandidateSourcingFilters) => void;
}

export const EditFiltersModal: FC<EditFiltersModalProps> = ({
  filterOptions = {},
  filters,
  isOpen,
  onClose,
  onFiltersChange
}) => {
  const {
    localFilters,
    newValues,
    hasChanges,
    handleAddFilter,
    handleRemoveFilter,
    handleValueChange,
    resetState
  } = useFilterState({ filters, isOpen });

  const handleSave = () => {
    onFiltersChange(localFilters);
    onClose();
  };

  const handleCancel = () => {
    resetState();
    onClose();
  };

  const getFilterOptions = (key: FilterKey): string[] => {
    if (key === 'years_of_experience') {
      return yearsOfExperienceOptions;
    }
    return filterOptions[key as keyof typeof filterOptions] || [];
  };

  const renderFilterSection = (key: FilterKey) => {
    const values = localFilters[key];
    const newValue = newValues[key] || '';
    const options = getFilterOptions(key);

    return (
      <FilterSection
        key={key}
        filterKey={key}
        values={values}
        newValue={newValue}
        options={options}
        onValueChange={(value) => handleValueChange(key, value)}
        onAdd={(value) => handleAddFilter(key, value)}
        onRemove={handleRemoveFilter}
      />
    );
  };

  return (
    <StyledModal
      isOpen={isOpen}
      handleClose={handleCancel}
      styleOverrides={{
        maxWidth: '600px',
        maxHeight: '80vh'
      }}
      label="edit-filters-modal"
      modalTitle="Edit Filters"
      subHeaderStyling={false}
    >
      <Box sx={{ ...sharedClasses.mainModalContent, height: '100%' }}>
        <Box sx={{ mb: 3 }}>
          {Object.keys(filterToLabel).map((key) => renderFilterSection(key as FilterKey))}
        </Box>
      </Box>
      <ModalFooterButtons
        primaryButtonText="Save Changes"
        primaryButtonCallback={handleSave}
        primaryButtonDisabled={!hasChanges}
        secondaryButtonText="Cancel"
        secondaryButtonCallback={handleCancel}
        primaryButtonID="save-filters-button"
        secondaryButtonID="cancel-filters"
      />
    </StyledModal>
  );
};

export default EditFiltersModal;
